import React, { useEffect, useState } from 'react';
import "./NavMenu.css";
import { Link } from 'react-router-dom';
import { ReactComponent as FatigueAssessmentIcon } from "assets/icons/fatigue-assessment.svg";
import { ReactComponent as ReportingIcon } from "assets/icons/reports-filled.svg";
import { ReactComponent as AppsIcon } from "assets/icons/apps-filled.svg";
import { ReactComponent as FormsIcon } from "assets/icons/rb-network-rules-forms.svg";
import { ReactComponent as QuickLinksIcon } from "assets/icons/link-filled.svg";
import { ReactComponent as TakeFiveIcon } from "assets/icons/take-five.svg";
import { ReactComponent as OperationalFormsIcon } from "assets/icons/operational-forms.svg";
import { ReactComponent as UserApprovalsIcon } from "assets/icons/check-verified.svg";
import { QuickLinksData, IQuickLinkItem } from '../ui/QuickLinks';
import { ApplicationData, IApplicationItem } from '../ui/Applications';
import {
    DocumentManagementDocumentViewTraditional, DocumentManagementDocumentViewCBTC, getClassName, IDocumentModule,
    RailRulebookDocumentModuleTraditional, RailRulebookDocumentModuleCBTC, RailDocumentDocumentModuleTraditional,
    RailDocumentDocumentModuleCBTC
} from '../ui/DocumentRules';
import { GeneralAdministrationModule } from 'components/portal/administration/general-administration-data';
import {
    fatigueAssessmentRoute,
    fatigueAssessmentOnBehalfRoute,
    fatigueAssessmentAssignRoute,
    fatigueAssessmentBlankFormRoute,
    fatigueAssessmentDashboardPathRoute
} from 'components/portal/fatigueAssessment/FatigueAssessmentRoutes';
import { IApplicationModule } from '../ui/IApplicationModule';
import { UserManagementModule } from '../portal/administration/user-management-data';
import { Grid2, GridSize } from "@mui/material";
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined, Settings } from '@mui/icons-material';
import { ReactComponent as ArrowForward } from "assets/icons/arrow_forward.svg";
import { userHasPermission } from 'hooks/useRoles';
import { useAllDocumentTypes } from 'hooks/useDocumentType';

import { gnhUsersSubmittedFormListRoute } from '../portal/drivingExemption/DrivingExemptionRoutes';
import useSelector from '../../hooks/useSelector';
import { operationalFormsRoute } from '../portal/OperationalForms/OperationalFormsRoutes';
import { takeFiveRoute } from '../portal/takeFive/TakeFiveRoutes';
import { userApprovalsPendingPathRoute } from '../portal/userApprovals/UserApprovalsRoutes';
const underConstructionRoute = "/underconstruction";

type NavMenuProps = {
    admin: boolean,
    onFollowLink: () => void,
    cbtcTrained: boolean
}

interface IMenuItem {
    title: string,
    className?: string,
    children?: IMenuItem[],
    maxDisplayedChildren?: number,
    roles?: string[],
    hideInMenuTile?: boolean,
    url?: string,
    permission?: string,
    external?: boolean,
    target?: string,
    documentTypeId?: number,
    icon?: any
}

interface IMenuTile extends IMenuItem {
    modulePrefix: string,
    permission?: string
}

var toggle: () => void;

const LinkAndClose = ({ ...props }) => (
    <Link to={props.to} {...props} onClick={toggle} />
);

const CreateMenuForAppModule = (appMod: IApplicationModule): IMenuItem => {
    return {
        title: appMod.title,
        className: appMod.className,
        url: appMod.url,
        icon: appMod.icon,
        children: appMod.children?.filter(c => ((c.hideInMenuTile ?? false) === false)).map(c => CreateMenuForAppModule(c))
    } as IMenuItem;
};

const createAdministrationTile = (cbtcCompetent: boolean) => {
    return {
        title: "Administration",
        className: "administration",
        modulePrefix: "um",
        icon: Settings,
        children: [
            {
                title: UserManagementModule.title,
                className: UserManagementModule.className,
                url: UserManagementModule.url,
                icon: UserManagementModule.icon
            },
            {
                title: "User Approvals",
                className: "user-approvals",
                url: userApprovalsPendingPathRoute.path,
                icon: UserApprovalsIcon,
                permission: userApprovalsPendingPathRoute.permission
            },
            {
                title: "Document Management",
                className: "document-management",
                url: DocumentManagementDocumentViewTraditional.url,
                icon: DocumentManagementDocumentViewTraditional.icon
            },
            {
                title: "MBS Document Management",
                className: "document-management",
                url: DocumentManagementDocumentViewCBTC.url,
                icon: DocumentManagementDocumentViewCBTC.icon,
                permission: "web.cbtc.user.edit"
            },
            CreateMenuForAppModule(GeneralAdministrationModule)
        ]
    } as IMenuTile;
}

const CreateTileForDocumentModule = (docs: IDocumentModule) => {
    let children = docs.filters.map(dt => {
        return {
            documentTypeId: dt.id,
            title: dt.name,
            className: dt.className,
            url: dt.url,
            icon: dt.icon,
            children: dt.subFilters?.map(st => {
                return {
                    title: st.name,
                    className: st.className,
                    url: st.url,
                    icon: st.icon
                } as IMenuItem
            })
        }
    }) as IMenuItem[];

    let tiles = {
        title: docs.name,
        className: getClassName(docs),
        modulePrefix: docs.prefix,
        url: docs.url,
        icon: docs.icon,
        children: children,
        maxDisplayedChildren: docs.maxDisplayed
    } as IMenuTile;

    return tiles;
};

// Traditional
const RailRulebookTileDataTraditional = CreateTileForDocumentModule(RailRulebookDocumentModuleTraditional);
const RailDocumentsTileDataTraditional = CreateTileForDocumentModule(RailDocumentDocumentModuleTraditional);

// CBTC
const RailRulebookTileDataCBTC = CreateTileForDocumentModule(RailRulebookDocumentModuleCBTC);
const RailDocumentsTileDataCBTC = CreateTileForDocumentModule(RailDocumentDocumentModuleCBTC);

const FatigueAssessmentTileData = {
    title: "Fatigue Assessment",
    className: "fatigue-assessment",
    modulePrefix: "fa",
    icon: FatigueAssessmentIcon,
    url: fatigueAssessmentRoute.path,
    children: [
        {
            title: fatigueAssessmentBlankFormRoute.breadCrumb,
            icon: FatigueAssessmentIcon,
            url: fatigueAssessmentBlankFormRoute.path
        },
        {
            title: fatigueAssessmentOnBehalfRoute.breadCrumb,
            icon: FatigueAssessmentIcon,
            url: fatigueAssessmentOnBehalfRoute.path,
            permission: fatigueAssessmentOnBehalfRoute.permission
        },
        {
            title: fatigueAssessmentAssignRoute.breadCrumb,
            icon: FatigueAssessmentIcon,
            url: fatigueAssessmentAssignRoute.path,
            permission: fatigueAssessmentAssignRoute.permission
        },
        {
            title: fatigueAssessmentDashboardPathRoute.breadCrumb,
            icon: FatigueAssessmentIcon,
            url: fatigueAssessmentDashboardPathRoute.path,
            permission: fatigueAssessmentDashboardPathRoute.permission
        }
    ]
} as IMenuTile;

const ReportingTileData = {
    title: "Reporting",
    className: "reporting",
    modulePrefix: "rm",
    icon: ReportingIcon,
    children: [
        {
            title: "User / Document reporting",
            url: "/reporting/user-document",
            permission: "web.admin.user.reporting",
            icon: ReportingIcon
        },
        {
            title: "Fatigue Assessment reporting",
            url: "/reporting/fatigue-assessment",
            permission: "web.admin.fatigue-assessment.shift-summary-dashboard",
            icon: ReportingIcon
        },
        {
            title: "Login History reporting",
            url: "/reporting/login-history",
            permission: "web.admin.user.reporting",
            icon: ReportingIcon
        },
        {
            title: "GNH Exemption reporting",
            url: "/reporting/gnh-exemption",
            permission: "web.admin.gnh-exemption.exemption-request-dashboard",
            icon: ReportingIcon
        },
        {
            title: "D2 Document reporting",
            url: "/reporting/d2-document-update",
            permission: "web.admin.d2-document.d2-imported-document",
            icon: ReportingIcon
        },
        {
            title: "Take 5 reporting",
            url: "/reporting/take-five",
            icon: ReportingIcon
        },
        {
            title: "Train Event Record reporting",
            url: "/reporting/train-event-record",
            icon: ReportingIcon
        }
    ]
} as IMenuTile;

const toApplicationMenuItem = (app: IApplicationItem) => {
    return {
        title: app.name,
        className: "ql" + app.className,
        url: app.url,
        external: true,
        target: "_blank",
        icon: app.icon
    } as IMenuItem;
};

const ApplicationTileData = {
    title: "Apps",
    className: "applications",
    modulePrefix: "am",
    icon: AppsIcon,
    children: ApplicationData.filter(app => app.url).map(toApplicationMenuItem)    
} as IMenuTile;

const ApplicationTileDataGNH = {
    title: "GNH & LV Exemptions",
    className: "exemption-forms",
    modulePrefix: "am",
    icon: FormsIcon,
    url: gnhUsersSubmittedFormListRoute.path,
    children: [
        {
            title: "Exemption Forms",
            icon: FormsIcon,
            url: gnhUsersSubmittedFormListRoute.path
        }
    ]
} as IMenuTile;

const ApplicationTileDataTakeFive = {
    title: "Digital Take 5",
    className: "take-five",
    modulePrefix: "am",
    icon: TakeFiveIcon,
    url: takeFiveRoute.path,
    children: [
        {
            title: "Take 5 Home",
            icon: TakeFiveIcon,
            url: takeFiveRoute.path
        }
    ]
} as IMenuTile;

const ApplicationTileDataOperationForms = {
    title: "Operational Forms",
    className: "operational-forms",
    modulePrefix: "am",
    icon: OperationalFormsIcon,
    url: operationalFormsRoute.path
} as IMenuTile;

const toQuickLinkMenuItem = (ql: IQuickLinkItem) => {
    return {
        title: ql.title,
        className: "ql" + ql.className,
        url: ql.url,
        external: ql.url.startsWith("http"),
        target: "_blank",
        icon: ql.icon
    } as IMenuItem;
};

const QuickLinksTileData = {
    title: "Quick Links",
    className: "quick-links",
    modulePrefix: "ql",
    icon: QuickLinksIcon,
    children: QuickLinksData.map(toQuickLinkMenuItem)
} as IMenuTile;

const MenuItem = (props: { item: IMenuItem }) => {
    const Icon = props.item.icon;

    const hasPermission = () => {
        var perm = userHasPermission(props.item.permission);
        return perm;
    }

    return (
        <>
            {hasPermission() &&
                <li className={(props.item.className ?? "") + " menu-item"}>
                    <div className="menu-item-no-children">
                        {Icon &&
                            <Icon />
                        }

                        {props.item.external &&
                            <a target={props.item.target} rel="noopener noreferrer" href={props.item.url}>
                                {props.item.title}<span className="menu-item-arrow"><ArrowForward /></span>
                            </a>
                        }
                        {!props.item.external &&

                            <LinkAndClose to={props.item.url ?? underConstructionRoute}>
                                {props.item.title}<span className="menu-item-arrow"><ArrowForward /></span>
                            </LinkAndClose>
                        }
                    </div>
                    {props.item.children &&
                        <ul>
                            {props.item.children.filter(c => (c.hideInMenuTile ?? false === false)).map((subItem, idx) =>
                                <MenuItem key={idx} item={subItem} />)}
                        </ul>
                    }
                </li>
            }
        </>
    )
};

const MenuPanel = (props: { item: IMenuTile }) => {
    const Icon = props.item.icon;

    const documentTypeAllowed = (docTypeId: number | undefined) => {
        if (!docTypeId) {
            return true;
        }

        let isAllowed = useAllDocumentTypes().some(type => type.typeId === docTypeId);
        return isAllowed;
    }

    const hasPermission = () => {
        var perm = userHasPermission(props.item.permission);
        return perm;
    }

    const viewAllItem = {
        title: 'View all...',
        url: props.item.url,
        icon: props.item.icon
    } as IMenuItem;

    const menuItems = () => {
        if (!props?.item?.children) {
            return [] as IMenuItem[];
        }

        let items = props.item.children
            .filter(c =>
                (c.hideInMenuTile ?? false === false) &&
                documentTypeAllowed(c.documentTypeId)
            );

        if (props.item.maxDisplayedChildren && items.length > props.item.maxDisplayedChildren) {
            items = items.slice(0, props.item.maxDisplayedChildren);
            items.push(viewAllItem);
        }

        return items;
    }

    return (
        <>
            {hasPermission() &&
                <div className={"menu-panel"}>
                    <div className={props.item.modulePrefix}>
                        <div className="root-menu-item">
                            <Icon className="hover-menu-icon" />

                            {props.item.url &&
                                <LinkAndClose to={props.item.url}>{props.item.title}</LinkAndClose>
                            }

                            {!props.item.url &&
                                <span>{props.item.title}</span>
                            }
                        </div>

                        <ul className={props.item.title === "Quick Links" || props.item.title === "Apps" ? "quick-links-panel" : ""}>
                            {menuItems().map((subItem, idx) =>
                                <MenuItem key={idx} item={subItem} />
                            )}
                        </ul>

                    </div>
                </div>
            }
        </>
    );
};

interface IMenuTileProps {
    item: IMenuTile,
    currentMenu: string,
    setCurrentMenu: (name: string) => void,
    largeTile?: boolean,
    columns: GridSize,
    miniTile?: boolean
}

const MenuTile = (props: IMenuTileProps) => {
    const Icon = props.item.icon;

    const isExpanded = () => (props.currentMenu === props.item.title);

    const toggle = () => {
        if (isExpanded()) {
            props.setCurrentMenu("");
        } else {
            props.setCurrentMenu(props.item.title);
        }
    };

    return (
        <Grid2 size={{ xs: 12, md: props.columns }}>
            <div className={"tile " + props.item.className + (isExpanded() ? " expanded" : " collapsed") + (props.largeTile ? " large-tile" : "") + (props.miniTile ? " mini-tile" : "")} >
                <div className="tile-body" onClick={toggle}>
                    <Icon className="icon" />
                    <div className="label">{props.item.title}</div>
                    <div className="caret">{isExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}</div>
                </div>
                <MenuPanel item={props.item} />
            </div>
        </Grid2>
    );
};

const NavMenu = (props: NavMenuProps) => {
    let size = 0 as GridSize;
    if (props.admin && props.cbtcTrained) {
        size = 3 as GridSize;
    } else if ((!props.admin && props.cbtcTrained) || (props.admin && !props.cbtcTrained)) {
        size = 4 as GridSize;
    } else if (!props.admin && !props.cbtcTrained) {
        size = 6 as GridSize;
    }


    toggle = props.onFollowLink;
    const [currentMenu, setCurrentMenu] = useState("");
    const gnhFeatureToggleEnabled = useSelector(state => state.gnhFormsFeatureToggle);
    const [gnhFormsFeatureEnabled, setGNHFormsFeatureEnabled] = useState(false);

    useEffect(() => {
        setGNHFormsFeatureEnabled(gnhFeatureToggleEnabled);
        if (gnhFeatureToggleEnabled) {
            ReportingTileData.children = ReportingTileData.children
        }
    }, [gnhFeatureToggleEnabled]);

    return (
        <Grid2 container direction={"column"} className={"tile-container" + (props.admin && props.cbtcTrained ? " tile-container-cbtc" : " tile-container-traditional")} >
            <MenuTile
                columns={size}
                currentMenu={currentMenu}
                setCurrentMenu={setCurrentMenu}
                item={RailRulebookTileDataTraditional} />
            <MenuTile
                columns={size}
                currentMenu={currentMenu}
                setCurrentMenu={setCurrentMenu}
                item={RailDocumentsTileDataTraditional} />
            {props.cbtcTrained &&
                <MenuTile
                    columns={size}
                    currentMenu={currentMenu}
                    setCurrentMenu={setCurrentMenu}
                    item={RailRulebookTileDataCBTC} />
            }
            {props.cbtcTrained &&
                <MenuTile
                    columns={size}
                    currentMenu={currentMenu}
                    setCurrentMenu={setCurrentMenu}
                    item={RailDocumentsTileDataCBTC} />
            }
            <MenuTile
                columns={size}
                currentMenu={currentMenu}
                setCurrentMenu={setCurrentMenu}
                item={FatigueAssessmentTileData} />

            {props.admin &&
                <MenuTile
                    columns={size}
                    currentMenu={currentMenu}
                    setCurrentMenu={setCurrentMenu}
                item={ReportingTileData} />
            }

            {props.admin &&
                <MenuTile
                columns={size}
                currentMenu={currentMenu}
                setCurrentMenu={setCurrentMenu}
                item={createAdministrationTile(props.cbtcTrained)} />
            }

            <MenuTile
                miniTile={true}
                columns={size}
                currentMenu={currentMenu}
                setCurrentMenu={setCurrentMenu}
                item={ApplicationTileData} />

            <MenuTile
                miniTile={true}
                columns={size}
                currentMenu={currentMenu}
                setCurrentMenu={setCurrentMenu}
                item={QuickLinksTileData} />

            {gnhFormsFeatureEnabled &&
                <MenuTile
                    columns={size}
                    currentMenu={currentMenu}
                    setCurrentMenu={setCurrentMenu}
                    item={ApplicationTileDataGNH} />
            }
            <MenuTile
                columns={size}
                currentMenu={currentMenu}
                setCurrentMenu={setCurrentMenu}
                item={ApplicationTileDataTakeFive} />
            <MenuTile
                columns={size}
                currentMenu={currentMenu}
                setCurrentMenu={setCurrentMenu}
                item={ApplicationTileDataOperationForms} />
        </Grid2>
    );
}

export default NavMenu;